import React, {FC, useCallback, useEffect, useState} from 'react'

import {yupResolver} from '@hookform/resolvers/yup'
import cn from 'classnames/bind'
import {FormProvider, useForm} from 'react-hook-form'
import * as yup from 'yup'

import FormHeader from 'components/blocks/FormHeader'
import Button from 'components/elements/Button'
import FieldCheckbox from 'components/elements/fields/FieldCheckbox'
import FieldInput from 'components/elements/fields/FieldInput'
import FieldTextarea from 'components/elements/fields/FieldTextarea'
import MessageBlock from 'components/elements/MessageBlock'
import apiService from 'services/api.service'
import {useAuthStore} from 'src/utils/contexts/RootStore.context'
import useAsyncFlow from 'src/utils/hooks/useAsyncFlow'
import validations, {messages} from 'src/utils/validations'

import s from './ReplyForm.module.scss'
import RequiredTip from '../RequiredTip'

const cx = cn.bind(s)

const schema = yup
	.object({
		comment: yup.string().trim().required(messages.required),
		name: yup.string().trim().required(messages.required),
		website: validations.getURLValidator('web site'),
		email: yup.string().email('please enter a valid email').required(messages.required),
		isSave: yup.bool().default(false),
	})
	.required()

interface IReplyFormProps {
	className?: string
}

const ReplyForm: FC<IReplyFormProps> = ({className}) => {
	const [successMessage, setsuccessMessage] = useState('')
	const [replyFlow, {isLoading, errorMessage}] = useAsyncFlow<typeof apiService.reply>(
		apiService.reply,
	)
	const authStore = useAuthStore()

	const methods = useForm<yup.TypeOf<typeof schema>>({
		resolver: yupResolver(schema),
		defaultValues: {
			comment: '',
			name: '',
			website: '',
			email: '',
			isSave: false,
		},
	})

	useEffect(() => {
		const replyData = JSON.parse(localStorage.getItem('replyStore') as string)
		if (replyData) {
			const {isSave} = replyData
			if (isSave) {
				methods.reset(replyData)
				return
			}
		}
		methods.reset({
			email: authStore.user?.email || '',
			name: authStore.user?.firstName || '',
		})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const onSubmit = useCallback(async (data: yup.TypeOf<typeof schema>) => {
		const {isSave, email, name, comment, website} = data
		await replyFlow({
			name: name || '',
			email: email || '',
			comment: comment || '',
			website: website || '',
		})({
			onSuccess: () => {
				setsuccessMessage(
					'We have received your message and would like to thank you for writing to us.',
				)
				if (isSave) {
					localStorage.setItem('replyStore', JSON.stringify({email, name, website, isSave}))
				} else {
					localStorage.removeItem('replyStore')
				}
			},
		})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	return (
		<div className={cx('wrapper', className)}>
			<FormProvider {...methods}>
				<form className={s.form}>
					<FormHeader
						className={cx('header')}
						title="LEAVE A REPLY"
						subTitle={
							<div className={cx('subtitle')}>
								<p className={cx('subtitle-text')}>Your email address will not be published.</p>
								<RequiredTip />
							</div>
						}
						message={errorMessage || methods.formState.errors}
					/>

					{successMessage && (
						<MessageBlock type="success" className={cx('message')} message={successMessage} />
					)}

					<FieldTextarea
						name="comment"
						label="Comment"
						placeholder="Write comment"
						rows={4}
						required
						className={cx('input-spaces', 'form__comment')}
					/>
					<div className={cx('field-group')}>
						<FieldInput
							wrapperClassName="input-spaces"
							name="name"
							label="Name"
							placeholder="Write your name"
							required
						/>
						<FieldInput
							wrapperClassName="input-spaces"
							name="website"
							label="Website "
							placeholder="Write your website"
						/>
					</div>
					<FieldInput
						wrapperClassName="input-spaces"
						name="email"
						type="email"
						label="Email address"
						placeholder="Write your email"
						required
					/>

					<FieldCheckbox
						classNameWrapper={cx('form__save')}
						name="isSave"
						type="checkbox"
						label="Save my name, email, and website in this browser for the next time I comment."
					/>

					<div className={cx('buttons')}>
						<Button
							className={cx('btn')}
							disabled={methods.formState.isSubmitted && !methods.formState.isValid}
							size="big"
							isLoading={isLoading}
							onClick={methods.handleSubmit(onSubmit)}>
							post comment
						</Button>
					</div>
				</form>
			</FormProvider>
		</div>
	)
}

ReplyForm.displayName = 'ReplyForm'

export default ReplyForm
